import { lazy, Suspense, useContext } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { ErrorBoundary } from '@/component/Error';
import { Skeleton } from '@/component/Skeleton';
import { useHasPermission } from '@/feature/permissions/PermissionsProvider';
import { NotFound } from '../../app/routing/NotFound';
import {
  activitiesPath,
  alarmsPath,
  basicsPath,
  discoverDataPath,
  equipmentDashboardPath,
  equipmentDataPath,
  equipmentPartsPath,
  insightsPath,
  noticeBoardPath,
} from '../../pathDefinitions';
import { PageJotaiProvider } from '../components/Jotai/JotaiProvider';
import { EquipmentTimeZoneProvider } from '../lib/timezone';
import detailsContext from './detailsContext';
import DetailsContextProvider from './DetailsContextProvider';

const ActivitiesList = lazy(() => import('../activity/list/ActivitiesList'));
const Alarms = lazy(() => import('../alarms/Alarms'));
const Basics = lazy(() => import('../basics/Basics'));
const Parts = lazy(() => import('../parts/Parts'));
const Dashboard = lazy(() =>
  import('../dashboards2/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  }))
);
const Data = lazy(() => import('../data/Data'));
const DiscoverData = lazy(() => import('../discover/DiscoverData'));
const Insights = lazy(() => import('../insights/Insights'));
const NoticeBoard = lazy(() => import('../messages/NoticeBoard'));

interface EquipmentDetailsProps {
  equipmentIds: string[];
}

const Details = ({ equipmentIds }: EquipmentDetailsProps) => {
  const content = (
    <PageJotaiProvider>
      <SubRoutes />
    </PageJotaiProvider>
  );

  return (
    <ErrorBoundary>
      <DetailsContextProvider selected={equipmentIds}>
        <EquipmentTimeZoneProvider>{content}</EquipmentTimeZoneProvider>
      </DetailsContextProvider>
    </ErrorBoundary>
  );
};

export default Details;

function SubRoutes() {
  const { selectedEquipment, isLoading } = useContext(detailsContext);
  const hasEquipmentData = useHasPermission('eqmnt.trends:r');
  const redirectRoute = hasEquipmentData ? equipmentDashboardPath : basicsPath;

  if (!selectedEquipment) {
    if (!isLoading) {
      return <>something went wrong</>;
    }
  }

  return (
    <Suspense
      fallback={<Skeleton sx={{ m: 2, height: 36 }} variant="rectangular" />}
    >
      <Routes>
        <Route
          path="/"
          element={
            <Navigate to={redirectRoute + window.location.search} replace />
          }
        />
        <Route
          path={equipmentDashboardPath}
          element={
            <ErrorBoundary>
              <Dashboard />
            </ErrorBoundary>
          }
        />
        <Route
          path={equipmentDataPath}
          element={
            <ErrorBoundary>
              <Data />
            </ErrorBoundary>
          }
        />
        <Route
          path={discoverDataPath}
          element={
            <ErrorBoundary>
              <DiscoverData />
            </ErrorBoundary>
          }
        />
        <Route
          path={alarmsPath}
          element={
            <ErrorBoundary>
              <Alarms />
            </ErrorBoundary>
          }
        />
        <Route
          path={insightsPath}
          element={
            <ErrorBoundary>
              <Insights />
            </ErrorBoundary>
          }
        />
        <Route
          path={noticeBoardPath}
          element={
            <ErrorBoundary>
              <NoticeBoard />
            </ErrorBoundary>
          }
        />
        <Route
          path={activitiesPath}
          element={
            <ErrorBoundary>
              <ActivitiesList />
            </ErrorBoundary>
          }
        />
        <Route
          path={basicsPath}
          element={
            <ErrorBoundary>
              <Basics />
            </ErrorBoundary>
          }
        />
        <Route
          path={equipmentPartsPath}
          element={
            <ErrorBoundary>
              <Parts />
            </ErrorBoundary>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
